// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import { type Component, For, Show } from "solid-js";
import type { FileUploadState } from "../useFileUpload";
import { FileUploadProgress } from "./FileUploadProgress";

/**
 * Props for FileUploadList component
 */
export interface FileUploadListProps {
  /** Array of upload states */
  uploads: FileUploadState[];
  /** Callback to remove/cancel upload */
  onRemove?: (uploadId: string) => void;
  /** Callback to retry failed upload */
  onRetry?: (uploadId: string) => void;
  /** Callback to clear all completed uploads */
  onClearCompleted?: () => void;
  /** Maximum height for the list */
  maxHeight?: string;
  /** Compact display mode */
  compact?: boolean;
}

/**
 * File upload list component that displays all active uploads with progress
 */
export const FileUploadList: Component<FileUploadListProps> = (props) => {
  const completedUploads = () =>
    props.uploads.filter((upload) => upload.status === "completed" || upload.status === "error");

  const activeUploads = () =>
    props.uploads.filter(
      (upload) => upload.status === "pending" || upload.status === "uploading" || upload.status === "processing",
    );

  return (
    <Show when={props.uploads.length > 0}>
      <div class="border border-surface-decoration dark:border-dark-surface-decoration rounded-lg bg-surface dark:bg-dark-surface shadow-sm">
        {/* Header */}
        <div class="flex items-center justify-between p-3 border-b border-surface-decoration dark:border-dark-surface-decoration">
          <h3 class="text-sm font-medium text-on-background dark:text-dark-on-background">
            File Uploads ({props.uploads.length})
          </h3>

          <Show when={completedUploads().length > 0 && props.onClearCompleted}>
            <button
              onClick={props.onClearCompleted}
              class="text-xs text-on-background/60 dark:text-dark-on-background/60 hover:text-on-background dark:hover:text-dark-on-background transition-colors"
            >
              Clear completed
            </button>
          </Show>
        </div>

        {/* Upload list */}
        <div class="space-y-2 p-3 overflow-y-auto" style={{ "max-height": props.maxHeight || "300px" }}>
          {/* Active uploads first */}
          <For each={activeUploads()}>
            {(upload) => (
              <FileUploadProgress
                upload={upload}
                onRemove={props.onRemove}
                onRetry={props.onRetry}
                compact={props.compact}
              />
            )}
          </For>

          {/* Completed uploads */}
          <For each={completedUploads()}>
            {(upload) => (
              <FileUploadProgress
                upload={upload}
                onRemove={props.onRemove}
                onRetry={props.onRetry}
                compact={props.compact}
              />
            )}
          </For>
        </div>

        {/* Overall progress for active uploads */}
        <Show when={activeUploads().length > 0}>
          <div class="border-t border-surface-decoration dark:border-dark-surface-decoration p-3">
            <div class="flex justify-between text-xs text-on-background/60 dark:text-dark-on-background/60 mb-1">
              <span>Overall Progress</span>
              <span>
                {activeUploads().length} of {props.uploads.length} files
              </span>
            </div>

            <div class="w-full bg-surface-decoration dark:bg-dark-surface-decoration rounded-full h-1">
              <div
                class="bg-primary dark:bg-dark-primary h-1 rounded-full transition-all duration-300 ease-out"
                style={{
                  width: `${activeUploads().reduce((sum, upload) => sum + upload.progress.percentage, 0) / Math.max(activeUploads().length, 1)}%`,
                }}
              />
            </div>
          </div>
        </Show>
      </div>
    </Show>
  );
};
